<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <!-- Welcome banner -->
        <div class="hero-card mb-4">
            <div class="hero-inner">
                <div class="hero-text">
                    <div class="hero-greeting">{{ greeting }}, {{ displayName }} 👋</div>
                    <div class="hero-date">{{ todayLabel }}</div>
                </div>
                <div class="hero-actions">
                    <b-button variant="light" size="sm" class="mr-2" @click="goto('/sales-docs/consignment/create')">
                        <i class="mdi mdi-plus mr-1"></i>{{ $t('module.consignment') }}
                    </b-button>
                    <b-button variant="outline-light" size="sm" class="mr-2" @click="goto('/sales-docs')">
                        {{ $t('module.salesDoc') }}
                    </b-button>
                    <b-button variant="outline-light" size="sm" @click="goto('/clients')">
                        {{ $t('module.client') }}
                    </b-button>
                </div>
            </div>
            <div class="hero-glow hero-glow-1"></div>
            <div class="hero-glow hero-glow-2"></div>
        </div>

        <!-- Follow-ups: what to DO today, before the numbers about what already happened -->
        <div v-if="$can('Read CRM')" class="row">
            <div class="col-md-6">
                <div
                    class="followup-card"
                    :class="overdueCount ? 'fu-overdue' : 'fu-clear'"
                    @click="goto('/activities')"
                >
                    <div class="fu-icon">
                        <i class="mdi" :class="overdueCount ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline'"></i>
                    </div>
                    <div class="fu-body">
                        <div class="fu-label">{{ $t('activityDash.overdue') }}</div>
                        <div class="fu-value">{{ overdueCount }}</div>
                        <div class="fu-sub">{{ overdueCount ? topOverdueLabel : $t('home.allClear') }}</div>
                    </div>
                    <i class="mdi mdi-chevron-right fu-arrow"></i>
                </div>
            </div>
            <div class="col-md-6">
                <div
                    class="followup-card"
                    :class="todayCount ? 'fu-today' : 'fu-clear'"
                    @click="goto('/activities')"
                >
                    <div class="fu-icon"><i class="mdi mdi-calendar-today"></i></div>
                    <div class="fu-body">
                        <div class="fu-label">{{ $t('activityDash.today') }}</div>
                        <div class="fu-value">{{ todayCount }}</div>
                        <div class="fu-sub">{{ todayCount ? topTodayLabel : $t('home.nothingToday') }}</div>
                    </div>
                    <i class="mdi mdi-chevron-right fu-arrow"></i>
                </div>
            </div>
        </div>

        <!-- Stat cards -->
        <div class="row">
            <div class="col-xl-3 col-md-6">
                <div class="stat-card stat-primary">
                    <div class="stat-icon"><i class="mdi mdi-cash-multiple"></i></div>
                    <div class="stat-body">
                        <div class="stat-label">Sales (MTD)</div>
                        <div class="stat-value">{{ fmtAmount(salesSummary && salesSummary.month_total) }}</div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="stat-card stat-info">
                    <div class="stat-icon"><i class="mdi mdi-file-document-multiple-outline"></i></div>
                    <div class="stat-body">
                        <div class="stat-label">Transactions (MTD)</div>
                        <div class="stat-value">{{ (salesSummary && salesSummary.month_count) || 0 }}</div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="stat-card stat-success">
                    <div class="stat-icon"><i class="mdi mdi-calendar-today"></i></div>
                    <div class="stat-body">
                        <div class="stat-label">Sales (Today)</div>
                        <div class="stat-value">{{ fmtAmount(salesSummary && salesSummary.today_total) }}</div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="stat-card stat-warning">
                    <div class="stat-icon"><i class="mdi mdi-bell-alert-outline"></i></div>
                    <div class="stat-body">
                        <div class="stat-label">Pending Approvals</div>
                        <div class="stat-value">{{ pendingApprovalsCount }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Charts -->
        <div class="row">
            <div class="col-xl-8">
                <div class="card panel-card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <h5 class="mb-0">Monthly Sales Trend</h5>
                            <small class="text-muted">Last {{ trendSeries[0].data.length }} months</small>
                        </div>
                        <apexchart
                            v-if="hasTrendData"
                            type="area"
                            height="300"
                            :options="trendChartOptions"
                            :series="trendSeries"
                        />
                        <div v-else class="empty-state">
                            <i class="mdi mdi-chart-line"></i>
                            <p class="mb-0">No trend data yet.</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-4">
                <div class="card panel-card">
                    <div class="card-body">
                        <h5 class="mb-2">Transaction Type Mix</h5>
                        <small class="text-muted d-block mb-2">From recent activity</small>
                        <apexchart
                            v-if="hasDocTypeData"
                            type="donut"
                            height="290"
                            :options="docTypeChartOptions"
                            :series="docTypeSeries"
                        />
                        <div v-else class="empty-state">
                            <i class="mdi mdi-chart-donut"></i>
                            <p class="mb-0">No documents yet.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Recent + Top debtors -->
        <div class="row">
            <div class="col-xl-8">
                <div class="card panel-card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h5 class="mb-0">Recent Transactions</h5>
                            <b-button variant="link" size="sm" class="p-0" @click="goto('/sales-docs')">
                                View all <i class="mdi mdi-arrow-right"></i>
                            </b-button>
                        </div>
                        <b-table
                            v-if="recentSalesDocs.length"
                            :items="recentSalesDocs"
                            :fields="recentFields"
                            small
                            responsive="sm"
                            hover
                            thead-class="table-light"
                            @row-clicked="onRecentClicked"
                            class="mb-0 clickable-table"
                        >
                            <template #cell(doc_no)="d">
                                <span class="font-weight-semibold">{{ d.item.doc_no }}</span>
                            </template>
                            <template #cell(doc_type)="d">
                                <b-badge :variant="badgeVariant(d.item.doc_type)" class="text-capitalize">
                                    {{ d.item.doc_type }}
                                </b-badge>
                            </template>
                            <template #cell(total)="d">
                                <span class="font-weight-semibold">{{ fmtAmount(d.item.total) }}</span>
                            </template>
                        </b-table>
                        <div v-else class="empty-state">
                            <i class="mdi mdi-file-document-outline"></i>
                            <p class="mb-0">No recent documents.</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-4">
                <div class="card panel-card">
                    <div class="card-body">
                        <h5 class="mb-3">Top Debtors</h5>
                        <ul v-if="topDebtors.length" class="debtor-list">
                            <li
                                v-for="(d, i) in topDebtors.slice(0, 6)"
                                :key="i"
                            >
                                <div class="debtor-rank">{{ i + 1 }}</div>
                                <div class="debtor-name text-truncate">
                                    {{ d.debtor_name || d.debtor_code }}
                                </div>
                                <div class="debtor-amount">{{ fmtAmount(d.total) }}</div>
                            </li>
                        </ul>
                        <div v-else class="empty-state">
                            <i class="mdi mdi-account-cash-outline"></i>
                            <p class="mb-0">No data yet.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { mapState } from "vuex";
import { LEAD_ACTIVITY_MODULE } from "@/state/modules/leadActivity";
import { leadActivityMethods } from "@/state/helpers";
import { sessionHelper } from "@/utils";
import { REPORT_MODULE } from "@/state/modules/report";
import { APPROVAL_MODULE } from "@/state/modules/approval";
import { reportMethods, approvalMethods, authMethods } from "@/state/helpers";

const recentFields = [
    { key: "doc_no", label: "Reference No" },
    { key: "doc_type", label: "Type" },
    {
        key: "debtor_name",
        label: "Debtor",
        formatter: (v, k, item) => v || item?.debtor_code || "—",
    },
    { key: "doc_date", label: "Date" },
    { key: "total", label: "Total", class: "text-right" },
];

const PRIMARY = "#6366F1";
const PALETTE = ["#6366F1", "#22C55E", "#F59E0B", "#06B6D4", "#EC4899", "#8B5CF6", "#EF4444"];

export default {
    components: { Layout, PageHeader },
    data() {
        return {
            title: i18n.t("module.home"),
            items: [{ text: i18n.t("module.home"), active: true }],
            recentFields,
            displayName:
                sessionHelper.getUserName() ||
                sessionHelper.getStaffUserName() ||
                "there",
        };
    },
    computed: {
        ...mapState(REPORT_MODULE, [
            "salesSummary",
            "topDebtors",
            "recentSalesDocs",
            "monthlyTrend",
        ]),
        ...mapState(APPROVAL_MODULE, { approvalList: (s) => s.list.data }),
        ...mapState(LEAD_ACTIVITY_MODULE, {
            overdueActivities: (s) => s.overdue,
            todayActivities: (s) => s.today,
        }),
        overdueCount() {
            return (this.overdueActivities || []).length;
        },
        todayCount() {
            return (this.todayActivities || []).length;
        },
        // A name beats a bare number — it turns the card into "call Ahmad".
        topOverdueLabel() {
            return this.previewLabel(this.overdueActivities);
        },
        topTodayLabel() {
            return this.previewLabel(this.todayActivities);
        },
        pendingApprovalsCount() {
            return (this.approvalList || []).filter(
                (a) => (a.status || "").toLowerCase() === "pending"
            ).length;
        },
        greeting() {
            const h = new Date().getHours();
            if (h < 12) return "Good morning";
            if (h < 18) return "Good afternoon";
            return "Good evening";
        },
        todayLabel() {
            return new Date().toLocaleDateString(undefined, {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
        trendSeries() {
            const rows = this.monthlyTrend || [];
            return [
                {
                    name: "Sales",
                    data: rows.map((r) => Number(r.total) || 0),
                },
            ];
        },
        hasTrendData() {
            return (this.monthlyTrend || []).length > 0;
        },
        trendChartOptions() {
            const labels = (this.monthlyTrend || []).map((r) => r.month);
            return {
                chart: { toolbar: { show: false }, sparkline: { enabled: false } },
                colors: [PRIMARY],
                stroke: { curve: "smooth", width: 3 },
                fill: {
                    type: "gradient",
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.45,
                        opacityTo: 0.05,
                        stops: [0, 90, 100],
                    },
                },
                dataLabels: { enabled: false },
                grid: { borderColor: "#EEF0F4", strokeDashArray: 4 },
                xaxis: {
                    categories: labels,
                    labels: { style: { colors: "#8993A4" } },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                },
                yaxis: {
                    labels: {
                        style: { colors: "#8993A4" },
                        formatter: (v) => this.fmtCompact(v),
                    },
                },
                tooltip: {
                    y: { formatter: (v) => this.fmtAmount(v) },
                },
            };
        },
        docTypeBreakdown() {
            const counts = {};
            (this.recentSalesDocs || []).forEach((d) => {
                const t = d.doc_type || "other";
                counts[t] = (counts[t] || 0) + 1;
            });
            return counts;
        },
        docTypeSeries() {
            return Object.values(this.docTypeBreakdown);
        },
        hasDocTypeData() {
            return this.docTypeSeries.length > 0;
        },
        docTypeChartOptions() {
            return {
                chart: { toolbar: { show: false } },
                labels: Object.keys(this.docTypeBreakdown).map((k) =>
                    k.charAt(0).toUpperCase() + k.slice(1)
                ),
                colors: PALETTE,
                legend: { position: "bottom", fontSize: "12px" },
                dataLabels: { enabled: true, style: { fontSize: "11px" } },
                stroke: { width: 0 },
                plotOptions: {
                    pie: {
                        donut: {
                            size: "68%",
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: "Transactions",
                                    color: "#8993A4",
                                    formatter: () => this.docTypeSeries.reduce((a, b) => a + b, 0),
                                },
                            },
                        },
                    },
                },
            };
        },
    },
    async created() {
        await Promise.all([
            this.getSalesSummary({}).catch(() => {}),
            this.getTopDebtors({ filter: { limit: 6 } }).catch(() => {}),
            this.getMonthlyTrend({}).catch(() => {}),
            this.getRecentSalesDocs({ filter: { limit: 8 } }).catch(() => {}),
            this.getAllApprovals({
                filter: { pageNumber: 1, pageSize: 50, searchArray: [] },
            }).catch(() => {}),
            // Only the agent's own follow-ups — both endpoints filter by assignee.
            this.$can("Read CRM") ? this.getOverdueActivities().catch(() => {}) : null,
            this.$can("Read CRM") ? this.getTodayActivities().catch(() => {}) : null,
        ]);
    },
    methods: {
        ...authMethods,
        ...reportMethods,
        ...approvalMethods,
        ...leadActivityMethods,
        // A name beats a bare number — turns the card into "call Ahmad".
        previewLabel(rows) {
            const list = rows || [];
            if (!list.length) return "";
            const first = list[0].subject || "";
            return list.length > 1
                ? i18n.t("home.andMore", [first, list.length - 1])
                : first;
        },
        goto(path) {
            if (this.$route.path !== path) this.$router.push(path);
        },
        onRecentClicked(item) {
            if (item?.doc_type && item?.doc_no) {
                this.$router.push(
                    `/sales-docs/${item.doc_type}/${item.doc_no}`
                );
            }
        },
        fmtAmount(v) {
            const n = Number(v);
            if (!isFinite(n)) return "—";
            return n.toLocaleString(undefined, {
                style: "currency",
                currency: "MYR",
                minimumFractionDigits: 2,
            });
        },
        fmtCompact(v) {
            const n = Number(v) || 0;
            if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
            if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1) + "K";
            return n.toFixed(0);
        },
        badgeVariant(t) {
            switch ((t || "").toLowerCase()) {
                case "consignment": return "primary";
                case "salesinvoice": return "success";
                case "cashsale": return "info";
                case "quotation": return "warning";
                case "salesorder": return "secondary";
                case "deliveryorder": return "dark";
                default: return "light";
            }
        },
    },
};
</script>

<style scoped>
.hero-card {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
    background: linear-gradient(135deg, #5E2FE0 0%, #7C4DFF 55%, #9C6AFF 100%);
    color: #fff;
    padding: 24px 28px;
    box-shadow: 0 12px 30px rgba(94, 47, 224, 0.25);
}
.hero-inner {
    position: relative;
    z-index: 2;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
}
.followup-card {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    border: 1px solid #e9ecef;
    border-left: 4px solid #adb5bd;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 16px;
    cursor: pointer;
    transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.followup-card:hover {
    box-shadow: 0 6px 18px rgba(33, 37, 41, 0.1);
    transform: translateY(-1px);
}
.fu-icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
}
.fu-body { flex: 1; min-width: 0; }
.fu-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #8993a4;
}
.fu-value { font-size: 24px; font-weight: 700; line-height: 1.1; color: #212529; }
.fu-sub {
    font-size: 11px;
    color: #8993a4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.fu-arrow { color: #ced4da; font-size: 20px; flex-shrink: 0; }

/* Overdue earns red; nothing else on this page is allowed to shout. */
.fu-overdue { border-left-color: #ef4444; }
.fu-overdue .fu-icon { background: rgba(239, 68, 68, 0.12); color: #ef4444; }
.fu-overdue .fu-value { color: #ef4444; }

.fu-today { border-left-color: #6366f1; }
.fu-today .fu-icon { background: rgba(99, 102, 241, 0.12); color: #6366f1; }

.fu-clear { border-left-color: #22c55e; }
.fu-clear .fu-icon { background: rgba(34, 197, 94, 0.12); color: #22c55e; }
.fu-clear .fu-value { color: #adb5bd; }

.hero-greeting {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.3px;
    margin-bottom: 4px;
}
.hero-date {
    font-size: 13px;
    opacity: 0.85;
}
.hero-actions .btn {
    border-radius: 999px;
    padding: 6px 14px;
    font-weight: 500;
}
.hero-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(40px);
    z-index: 1;
    pointer-events: none;
}
.hero-glow-1 {
    width: 220px; height: 220px;
    top: -80px; right: -40px;
    background: rgba(255,255,255,0.18);
}
.hero-glow-2 {
    width: 160px; height: 160px;
    bottom: -70px; left: 20%;
    background: rgba(255,255,255,0.10);
}

.stat-card {
    background: #fff;
    border-radius: 14px;
    padding: 18px 20px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 14px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05), 0 4px 12px rgba(15, 23, 42, 0.04);
    transition: transform .15s ease, box-shadow .15s ease;
    border-left: 4px solid transparent;
}
.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(15, 23, 42, 0.07), 0 12px 24px rgba(15, 23, 42, 0.06);
}
.stat-icon {
    width: 48px; height: 48px;
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 24px;
}
.stat-body { flex: 1; min-width: 0; }
.stat-label {
    text-transform: uppercase;
    letter-spacing: .04em;
    font-size: 11px;
    font-weight: 600;
    color: #8993A4;
    margin-bottom: 4px;
}
.stat-value {
    font-size: 22px;
    font-weight: 700;
    color: #1F2937;
    letter-spacing: -0.4px;
    word-break: break-word;
}
.stat-primary { border-left-color: #6366F1; }
.stat-primary .stat-icon { background: rgba(99,102,241,0.12); color: #6366F1; }
.stat-info { border-left-color: #06B6D4; }
.stat-info .stat-icon { background: rgba(6,182,212,0.12); color: #06B6D4; }
.stat-success { border-left-color: #22C55E; }
.stat-success .stat-icon { background: rgba(34,197,94,0.12); color: #22C55E; }
.stat-warning { border-left-color: #F59E0B; }
.stat-warning .stat-icon { background: rgba(245,158,11,0.14); color: #F59E0B; }

.panel-card {
    border: none;
    border-radius: 14px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05), 0 4px 12px rgba(15, 23, 42, 0.04);
}
.panel-card .card-body h5 {
    font-weight: 700;
    color: #1F2937;
    letter-spacing: -0.2px;
}

.clickable-table >>> tbody tr { cursor: pointer; }
.font-weight-semibold { font-weight: 600; }

.debtor-list {
    list-style: none;
    padding: 0;
    margin: 0;
}
.debtor-list li {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #F1F2F6;
}
.debtor-list li:last-child { border-bottom: none; }
.debtor-rank {
    width: 26px; height: 26px;
    border-radius: 8px;
    background: rgba(99,102,241,0.1);
    color: #6366F1;
    font-weight: 700;
    font-size: 12px;
    display: flex; align-items: center; justify-content: center;
}
.debtor-name {
    flex: 1; min-width: 0;
    font-weight: 500;
    color: #1F2937;
}
.debtor-amount {
    font-weight: 600;
    color: #1F2937;
}

.empty-state {
    text-align: center;
    padding: 32px 16px;
    color: #8993A4;
}
.empty-state i {
    font-size: 40px;
    display: block;
    margin-bottom: 8px;
    opacity: 0.6;
}
</style>
